/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

// Common constants
export const ACTIONS = {
  VIEW: 'view',
  TAP: 'tap',
  CLICK: 'click',
  TRACK_EVENT_NO_ECOMMERCE: 'trackEventNoEcommerce',
};

export const SCREEN_NAME = {
  searchWidget: 'Search widget',
  hotelSearchPage: 'Hotel Search Page V2',
  MapView: 'MapView Page',
};

export const LINE_OF_BUSINESS = {
  hotelsSA: 'Hotels SA',
};

// Custom Event Names
export const EVENT_NAMES = {
  SEARCH_WIDGET_VIEWED: 'aa_htl_searchwidget_viewed',
  SMART_FILL_INPUT: 'aa_htl_smartfill_input',
  SMART_FILL_RESULTS: 'view_item_list',
  SEARCH_BUTTON_CLICKED: 'aa_htl_search',
  COUPONS_VIEWED: 'aa_htl_coupons_viewed',
  COUPONS_CLAIMED: 'aa_htl_coupons_claimed',
  LAST_SEARCHED_VIEWED: 'aa_htl_lastsearched_viewed',
  LAST_SEARCHED_CLICKED: 'aa_htl_lastsearched_clicked',
  FEATURED_DESTINATION_VIEWED: 'view',
  FEATURED_DESTINATION_CLICKED: 'tap',
  HOTEL_DEALS_VIEWED: 'view_item_list',
  HOTEL_DEALS_CLICKED: 'aa_htl_recowidget_clicked',
  CHINA_DISCLAIMER_VIEWED: 'view',
};

// Smart Fill specific constants
export const PROVIDER = {
  TYPESENSE: 'Typesense',
  ELASTIC_SEARCH: 'ElasticSearch',
  // TODO: Determine how to identify provider (TypeSense vs ElasticSearch)
};

// Search result labels
export const LABELS = {
  NO_RESULTS: 'noresults',
};

// Source constants
export const SOURCE = {
  GENERAL: 'gnral',
};

// Hotel Banner specific constants
export const HOTEL_BANNER = {
  CATEGORY: 'Hotels SA|herobanner',
  EVENT_NAMES: {
    view: 'view',
    tap: 'tap',
  },
};

// Hotel Banner interfaces
export interface BannerViewParams {
  bannerName: string;
  position: number;
}

export interface BannerTapParams {
  bannerName: string;
  position: number;
}

// Search Button Click interface
export interface SearchButtonParams {
  destination: string;
  destinationcategory: string;
  destinationcountry: string;
  checkindate: string;
  checkoutdate: string;
  lengthofstay: number;
  rooms: number;
  totalpax: number;
  adult: number;
  child: number;
  infant: number;
  isMap?: boolean;
}

// Coupon Claim interface
export interface CouponClaimParams {
  couponId: string;
  position: number;
}

export interface CouponViewedParams {
  couponId: string;
  position: number;
}

// Last Searched Viewed interface
export interface LastSearchedViewedParams {
  destinations: Array<{
    destinationId: string;
    position: number;
  }>;
}

// Last Searched Viewed constants
export const LAST_SEARCHED_VIEWED = {
  CATEGORY: 'Hotels SA|hotelsearchwidget',
  ACTION: 'aa_htl_lastsearched_viewed',
};

// Last Searched Clicked interface
export interface LastSearchedClickedParams {
  destinationId: string;
  position: number;
}

// Last Searched Clicked constants
export const LAST_SEARCHED_CLICKED = {
  CATEGORY: 'Hotels SA|hotelsearchwidget',
  ACTION: 'aa_htl_lastsearched_clicked',
};

// Featured Destination Viewed interface
export interface FeaturedDestinationViewedParams {
  destinationCodes: string[];
}

// Featured Destination Viewed constants
export const FEATURED_DESTINATION_VIEWED = {
  CATEGORY: 'Hotels SA|featured destination',
};

// Featured Destination Clicked interface
export interface FeaturedDestinationClickedParams {
  destinationId: string;
}

// Featured Destination Clicked constants
export const FEATURED_DESTINATION_CLICKED = {
  CATEGORY: 'Hotels SA|featured destination',
};

// Hotel Deals Viewed item interface
export interface HotelDealsViewItem {
  item_id: string;
  item_name: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  quantity: string;
  price: number;
  index: number;
}

// Hotel Deals Viewed interface
export interface HotelDealsViewedParams {
  recommendationTitle: string;
  currency: string;
  items: HotelDealsViewItem[];
}

// Hotel Deals Viewed constants
export const HOTEL_DEALS_VIEWED = {
  CATEGORY_PREFIX: 'Hotels SA',
  LINE_OF_BUSINESS: 'Hotels SA',
  SCREEN_NAME: 'Hotel Search Page V2',
};

// Hotel Deals Clicked interface
export interface HotelDealsClickedParams {
  recommendationTitle: string;
  recommendationType: string;
  hotelId: string;
  hotelName: string;
  destination: string;
  destinationCategory: string;
  destinationCountry: string;
  checkInDate: string;
  checkOutDate: string;
  lengthOfStay: number;
  rooms: number;
  currency: string;
  price: number;
  strikeThroughPrice: string;
  totalPax: string;
  adult: number;
  child: number;
  infant: number;
}

// Hotel Deals Clicked constants
export const HOTEL_DEALS_CLICKED = {
  CATEGORY_PREFIX: 'Hotels SA',
  LINE_OF_BUSINESS: 'Hotels SA',
};

// China Disclaimer Viewed interface
export interface ChinaDisclaimerViewedParams {
  disclaimerMessage: string;
}

// China Disclaimer Viewed constants
export const CHINA_DISCLAIMER_VIEWED = {
  CATEGORY: 'Hotels SA|CN disclaimer',
  LINE_OF_BUSINESS: 'Hotels SA',
  SCREEN_NAME: 'Hotel Search Page V2',
};
